import React, { useEffect, useState } from 'react';
import { Form, Input, Button, message, Spin, Row, Col, Upload, Image } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { 
  SaveOutlined, 
  UploadOutlined, 
  DeleteOutlined, 
  GlobalOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  BgColorsOutlined,
  BankOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CardContainer } from '../../components/contract/CardContainer';
import { SectionHeader } from '../../components/contract/SectionHeader';
import { settingsApi, CompanySettings } from '../../services/api';

const CompanyBrandingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [logoUploading, setLogoUploading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => settingsApi.getCompany(),
  });

  useEffect(() => {
    if (settings) {
      form.setFieldsValue(settings);
    }
  }, [settings, form]);

  const saveMutation = useMutation({
    mutationFn: (values: Partial<CompanySettings>) => settingsApi.updateCompany(values),
    onSuccess: () => {
      message.success('Company settings saved successfully');
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    },
    onError: () => {
      message.error('Failed to save settings');
    },
  });

  const handleSubmit = (values: Partial<CompanySettings>) => {
    saveMutation.mutate(values);
  };

  const handleLogoUpload = async (file: RcFile) => {
    setLogoUploading(true);
    try {
      await settingsApi.uploadLogoFile(file);
      message.success('Logo uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    } catch {
      message.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
    return false;
  };

  const handleSignatureUpload = async (file: RcFile) => {
    setSignatureUploading(true);
    try {
      await settingsApi.uploadSignatureFile(file);
      message.success('Signature uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    } catch {
      message.error('Failed to upload signature');
    } finally {
      setSignatureUploading(false);
    }
    return false;
  };

  const handleDeleteLogo = async () => {
    try {
      await settingsApi.deleteLogo();
      message.success('Logo removed');
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    } catch {
      message.error('Failed to remove logo');
    }
  };

  const handleDeleteSignature = async () => {
    try {
      await settingsApi.deleteSignature();
      message.success('Signature removed');
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    } catch {
      message.error('Failed to remove signature');
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading settings..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <SectionHeader title="Error" subtitle="Unable to load settings" />
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '48px' }}>
      <div style={{ marginBottom: '32px' }}>
        <SectionHeader 
          title="Company Branding" 
          subtitle="Manage your company identity, contact details, and visual assets used across documents."
          icon={<BankOutlined style={{ fontSize: '24px' }} />}
          actions={
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={saveMutation.isPending}
              onClick={form.submit}
            >
              Save Changes
            </Button>
          }
          showBottomBorder
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              <CardContainer variant="secondary">
                <SectionHeader 
                  title="General Information" 
                  subtitle="Primary contact details for your organization"
                  icon={<GlobalOutlined />}
                  style={{ marginBottom: '24px' }}
                />
                
                <Form.Item
                  name="name"
                  label="Company Name"
                  rules={[{ required: true, message: 'Company name is required' }]}
                >
                  <Input size="large" placeholder="e.g. Rimtrade International" />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      name="email" 
                      label="Email Address"
                      rules={[{ type: 'email', message: 'Invalid email format' }]}
                    >
                      <Input prefix={<MailOutlined />} placeholder="contact@example.com" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="phone" label="Phone Number">
                      <Input prefix={<PhoneOutlined />} placeholder="(555) 123-4567" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="website" label="Website">
                  <Input prefix={<GlobalOutlined />} placeholder="https://www.rimtrade.com" />
                </Form.Item>
              </CardContainer>

              <CardContainer variant="secondary">
                <SectionHeader 
                  title="Headquarters" 
                  subtitle="Physical location address"
                  icon={<EnvironmentOutlined />}
                  style={{ marginBottom: '24px' }}
                />
                
                <Form.Item name="address" label="Street Address">
                  <Input placeholder="123 Trade Blvd, Suite 100" />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="city" label="City">
                      <Input placeholder="Sacramento" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="state" label="State / Province">
                      <Input placeholder="California" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="zip" label="Zip / Postal Code">
                      <Input placeholder="95814" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="country" label="Country">
                      <Input placeholder="United States" />
                    </Form.Item>
                  </Col>
                </Row>
              </CardContainer>
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              <CardContainer variant="tertiary">
                <SectionHeader 
                  title="Brand Theme" 
                  subtitle="Colors used in generated PDFs"
                  icon={<BgColorsOutlined />}
                  style={{ marginBottom: '24px' }}
                />
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="primaryColor" label="Primary Color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Form.Item name="primaryColor" noStyle>
                          <Input type="color" style={{ width: '40px', padding: 0, height: '40px', border: 'none', background: 'none' }} />
                        </Form.Item>
                        <Form.Item name="primaryColor" noStyle>
                          <Input placeholder="#000000" style={{ flex: 1 }} />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="secondaryColor" label="Secondary Color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <Form.Item name="secondaryColor" noStyle>
                          <Input type="color" style={{ width: '40px', padding: 0, height: '40px', border: 'none', background: 'none' }} />
                        </Form.Item>
                        <Form.Item name="secondaryColor" noStyle>
                          <Input placeholder="#000000" style={{ flex: 1 }} />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </CardContainer>

              <CardContainer variant="secondary">
                <SectionHeader 
                  title="Company Logo" 
                  subtitle="Displayed on headers and invoices"
                  style={{ marginBottom: '16px' }}
                />
                
                {settings?.logoUrl ? (
                  <div style={{ 
                    border: '1px dashed #d9d9d9', 
                    borderRadius: '8px', 
                    padding: '24px', 
                    textAlign: 'center',
                    background: '#fafafa'
                  }}>
                    <Image
                      src={settings.logoUrl}
                      alt="Company Logo"
                      style={{ maxHeight: '100px', objectFit: 'contain', marginBottom: '16px' }}
                    />
                    <div>
                      <Button danger icon={<DeleteOutlined />} onClick={handleDeleteLogo}>
                        Remove Logo
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Upload.Dragger
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleLogoUpload}
                    disabled={logoUploading}
                    style={{ background: '#fff' }}
                  >
                    {logoUploading ? <Spin /> : (
                      <div style={{ padding: '16px 0' }}>
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">Click or drag logo to upload</p>
                        <p className="ant-upload-hint">SVG, PNG, or JPG (Max 2MB)</p>
                      </div>
                    )}
                  </Upload.Dragger>
                )}
              </CardContainer>

              <CardContainer variant="secondary">
                <SectionHeader 
                  title="Digital Signature" 
                  subtitle="Used for automated signing"
                  style={{ marginBottom: '16px' }}
                />
                
                {settings?.signatureUrl ? (
                  <div style={{ 
                    border: '1px dashed #d9d9d9', 
                    borderRadius: '8px', 
                    padding: '24px', 
                    textAlign: 'center',
                    background: '#fafafa'
                  }}>
                    <Image
                      src={settings.signatureUrl}
                      alt="Signature"
                      style={{ maxHeight: '60px', objectFit: 'contain', marginBottom: '16px' }}
                    />
                    <div>
                      <Button danger icon={<DeleteOutlined />} onClick={handleDeleteSignature}>
                        Remove Signature
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Upload.Dragger
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleSignatureUpload}
                    disabled={signatureUploading}
                    style={{ background: '#fff' }}
                  >
                    {signatureUploading ? <Spin /> : (
                      <div style={{ padding: '16px 0' }}>
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">Click or drag signature to upload</p>
                        <p className="ant-upload-hint">Transparent PNG recommended</p>
                      </div>
                    )}
                  </Upload.Dragger>
                )}
              </CardContainer>

            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CompanyBrandingPage;
